import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import { formatBuyerColumnDisplay, parseDeliveryDateForUi } from './invoiceUtils';
import { COMPANY } from '../../../../constants/companyConstants';
import { imageToBase64Raw } from '../../InvoiceManagement/utils/invoicePrintUtils';
import signImage from '../../../../assets/sign.jpg';

const THIN = Object.freeze({ style: 'thin', color: { argb: 'FF000000' } });
const BORDER_ALL = Object.freeze({ top: THIN, left: THIN, bottom: THIN, right: THIN });
const FONT_BASE = Object.freeze({ name: '맑은 고딕', size: 10 });
const ALIGN_CENTER = Object.freeze({ horizontal: 'center', vertical: 'middle', wrapText: true });
const ALIGN_LEFT = Object.freeze({ horizontal: 'left', vertical: 'middle', wrapText: true });
const ALIGN_RIGHT = Object.freeze({ horizontal: 'right', vertical: 'middle' });
const FILL_HEADER = Object.freeze({ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } });
const FILL_SUPPLIER = Object.freeze({ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } });
const FILL_LIGHT = Object.freeze({ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFAFA' } });

const font = (overrides) => Object.freeze({ ...FONT_BASE, ...overrides });
const FONT_TITLE = font({ size: 20, bold: true });
const FONT_BOLD = font({ bold: true });
const FONT_BOLD_11 = font({ bold: true, size: 11 });
const FONT_BOLD_12 = font({ bold: true, size: 12 });
const FONT_BOLD_10 = font({ bold: true, size: 10 });
const FONT_DATA = font({ size: 9, bold: false });

const STYLE = Object.freeze({
    TITLE:          Object.freeze({ font: FONT_TITLE, alignment: ALIGN_CENTER }),
    LEFT_INFO:      Object.freeze({ font: FONT_BOLD, alignment: ALIGN_LEFT }),
    SUPPLIER_VERT:  Object.freeze({ font: FONT_BOLD_11, alignment: ALIGN_CENTER, border: BORDER_ALL, fill: FILL_SUPPLIER }),
    SUPPLIER_KEY:   Object.freeze({ font: FONT_BOLD, alignment: ALIGN_CENTER, border: BORDER_ALL, fill: FILL_LIGHT }),
    SUPPLIER_VAL:   Object.freeze({ font: FONT_BOLD, alignment: ALIGN_LEFT, border: BORDER_ALL }),
    CEO:            Object.freeze({ font: FONT_BOLD_12, alignment: ALIGN_CENTER, border: BORDER_ALL }),
    HEADER:         Object.freeze({ font: FONT_BOLD_10, alignment: ALIGN_CENTER, border: BORDER_ALL, fill: FILL_HEADER }),
    DATA_CENTER:    Object.freeze({ font: FONT_DATA, alignment: ALIGN_CENTER, border: BORDER_ALL }),
    DATA_RIGHT:     Object.freeze({ font: FONT_DATA, alignment: ALIGN_RIGHT, border: BORDER_ALL }),
    TOTAL_LABEL:    Object.freeze({ font: FONT_BOLD_11, alignment: ALIGN_CENTER, border: BORDER_ALL, fill: FILL_LIGHT }),
    TOTAL_VAL:      Object.freeze({ font: FONT_BOLD_10, alignment: ALIGN_RIGHT, border: BORDER_ALL, fill: FILL_LIGHT }),
    BANK:           Object.freeze({ font: FONT_BOLD_10, alignment: ALIGN_LEFT, border: BORDER_ALL })
});

const LAYOUT = Object.freeze({
    TITLE_ROW: 1,       TITLE_H: 32,
    GAP_TOP_ROW: 2,     GAP_H: 15,
    INFO_START_ROW: 3,  INFO_H: 20,
    GAP_MID_ROW: 6,
    HEADER_ROW: 7,      HEADER_H: 24,
    DATA_H: 20,
    TOTAL_H: 22,
    BANK_H: 75,
    MAX_ONE_LINE: 120,
    WIDTH_PADDING: 4,
    SUPPLIER_BLOCK_COLS: 8,
    LEFT_INFO_COLS: 3,
    SIGN_WIDTH: 160,
    SIGN_HEIGHT: 90
});

const CEO_NAME = COMPANY.ceo.nameKo;
const ADDRESS = COMPANY.address;

const SUPPLIER_ROWS = Object.freeze([
    ['Company Name', COMPANY.name],
    ['Address', ADDRESS],
    ['Business Type', 'Thread, Dyeing']
]);

const BANK_LINES = Object.freeze([
    `At bank : ${COMPANY.bank.name}`,
    `Bank Acc ($) : ${COMPANY.bank.accountUSD}  SWIFT CODE : ${COMPANY.bank.swiftCode}`,
    `Bank Acc (Rp) : ${COMPANY.bank.accountIDR}  SWIFT CODE : ${COMPANY.bank.swiftCode}`,
    `ACC NAME : ${COMPANY.bank.accountName}`
]);

const ALLOWED_COLUMN_ORDER = Object.freeze([
    'no', 'buyer', 'style', 'file', 'manager', 'delivery', 'item', 'size', 'color',
    'dyeingNo', 'deliveryDate', 'price', 'deliveryQty', 'priceSum'
]);

const HEADER_LABEL_OVERRIDES = Object.freeze({
    priceSum: 'TOTAL PRICE',
    deliveryDate: 'DELIVERY DATE',
    deliveryQty: 'QTY'
});

const COLUMN_MIN_WIDTHS = Object.freeze({
    no: 6, style: 30, file: 20, manager: 18, delivery: 27, item: 27, size: 12,
    color: 30, dyeingNo: 16, price: 17, deliveryDate: 18, deliveryQty: 10, priceSum: 15
});

const RIGHT_ALIGNED_IDS = new Set(['orderQty', 'price', 'priceSum', 'deliveryQty']);
const CURRENCY_SYMBOL = { USD: '$', IDR: 'Rp', KRW: '₩' };
const NUMBER_FORMATTER = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const NL = 10;

const toStr = (v) => (v == null || v === '' ? '' : String(v));
const getCurrencySymbol = (c) => CURRENCY_SYMBOL[c] ?? (c || '');
const numCorePattern = (value) => (Number.isInteger(Number(value)) ? '#,##0' : '#,##0.###');
const moneyNumFmtFor = (c, value) => {
    const sym = c ? getCurrencySymbol(c) : '';
    const core = numCorePattern(value);
    return sym ? `"${sym} "${core}` : core;
};
const qtyNumFmtFor = (value) => numCorePattern(value);

const formatNumber = (n) => {
    const v = Number(n);
    return Number.isFinite(v) ? NUMBER_FORMATTER.format(v) : '';
};

const formatDateDash = (d) => (d ? dayjs(d).format('MM/DD') : '');
const formatDateDot = (d) => (d ? dayjs(d).format('YYYY.MM.DD') : '');

const maxLineLen = (text) => {
    if (text == null || text === '') return 0;
    const s = String(text);
    const len = s.length;
    let max = 0;
    let start = 0;
    for (let i = 0; i <= len; i += 1) {
        if (i === len || s.charCodeAt(i) === NL) {
            const w = i - start;
            if (w > max) max = w;
            start = i + 1;
        }
    }
    return max;
};

const writeCell = (cell, value, style) => {
    cell.value = value;
    if (style) {
        if (style.font) cell.font = style.font;
        if (style.alignment) cell.alignment = style.alignment;
        if (style.border) cell.border = style.border;
        if (style.fill) cell.fill = style.fill;
    } else {
        cell.font = FONT_BASE;
        cell.alignment = ALIGN_CENTER;
    }
};

const getCellValueByColumnId = (id, item, index) => {
    switch (id) {
        case 'no': return String(index + 1);
        case 'customer': return item.vendor_name || '';
        case 'subOrderId': return toStr(item.sub_order_id);
        case 'buyer': return formatBuyerColumnDisplay(item.buyer_name, item.buyer_detail) || '';
        case 'style': return toStr(item.style_no);
        case 'file': return toStr(item.file_no);
        case 'dyeingNo': return toStr(item.dyeing_no);
        case 'delivery': return item.delivery_name || '';
        case 'manager': return item.manager_name || '';
        case 'item': return item.thread_name || '';
        case 'size': return item.size_name || '';
        case 'color': return item.color || '';
        case 'price': {
            if (item.price == null) return '';
            const sym = item.price_currency ? `${getCurrencySymbol(item.price_currency)} ` : '';
            return `${sym}${formatNumber(item.price)}`;
        }
        case 'orderQty': return item.order_qty != null ? formatNumber(item.order_qty) : '';
        case 'currency': return item.price_currency || '';
        case 'giReqNo': return item.gi_request_id != null && item.gi_request_id !== '' ? `GI-REQ-${String(item.gi_request_id).padStart(3, '0')}` : '';
        case 'giReqLineId': return toStr(item.gi_request_line_id);
        case 'giReq': return item.gi_request_planned_dispatch_at
            ? `${formatDateDash(item.gi_request_planned_dispatch_at)} (${formatNumber(item.gi_request_line_qty)})`
            : '';
        case 'deliveryDate': {
            const latestFromLines = (lines, field) => {
                if (!Array.isArray(lines) || lines.length === 0) return null;
                const sorted = lines
                    .map((l) => parseDeliveryDateForUi(l?.[field]))
                    .filter((p) => p != null)
                    .sort((a, b) => b.valueOf() - a.valueOf());
                return sorted.length > 0 ? sorted[0] : null;
            };
            const giReq = parseDeliveryDateForUi(item.gi_request_planned_dispatch_at);
            const packing = latestFromLines(item.packing_lot_lines, 'expected_gi_at');
            const giPost = latestFromLines(item.gi_post_lines, 'gi_time');
            const resolved = giReq || packing || giPost;
            return resolved ? resolved.format('YYYY-MM-DD') : '';
        }
        case 'deliveryQty': {
            const q = Number(item.gi_request_line_qty);
            return Number.isFinite(q) ? formatNumber(q) : '';
        }
        case 'giPost': return item.gi_post || '';
        case 'priceSum': {
            const unit = Number(item.price);
            if (!Number.isFinite(unit)) return '';
            const qty = Number(item.gi_request_line_qty || 0);
            const sym = item.price_currency ? `${getCurrencySymbol(item.price_currency)} ` : '';
            return `${sym}${formatNumber(qty * unit)}`;
        }
        case 'invoiceNo': return toStr(item.invoice_no);
        default: return '';
    }
};

const resolveColumns = (columns) => {
    const inputCols = Array.isArray(columns) ? columns : [];
    const byId = new Map();
    for (const c of inputCols) byId.set(c.id, c);
    if (byId.has('packing')) {
        byId.set('deliveryDate', { id: 'deliveryDate', label: 'DELIVERY DATE' });
        byId.set('deliveryQty', { id: 'deliveryQty', label: 'QTY' });
    }
    const cols = [];
    for (const id of ALLOWED_COLUMN_ORDER) {
        if (byId.has(id)) cols.push(byId.get(id));
    }
    if (cols.length === 0) cols.push({ id: 'buyer', label: 'Buyer' });
    return cols;
};

const createContext = (workbook, ws, cols) => {
    const lastColIdx = cols.length;
    const colLetters = new Array(lastColIdx);
    for (let i = 0; i < lastColIdx; i += 1) colLetters[i] = ws.getColumn(i + 1).letter;
    const maxLenByCol = new Array(lastColIdx).fill(0);
    return {
        workbook, ws, cols, lastColIdx, maxLenByCol,
        toLetter: (n) => colLetters[n - 1],
        lastColLetter: colLetters[lastColIdx - 1],
        measure(colIdx0, text) {
            const len = maxLineLen(text);
            if (len > maxLenByCol[colIdx0]) maxLenByCol[colIdx0] = len;
            return len;
        }
    };
};

const sumColumnWidths = (ws, start, end) => {
    let sum = 0;
    for (let i = start; i <= end; i += 1) sum += ws.getColumn(i).width || 0;
    return sum;
};

const writeTitle = (ctx) => {
    const { ws, lastColLetter } = ctx;
    ws.mergeCells(`A${LAYOUT.TITLE_ROW}:${lastColLetter}${LAYOUT.TITLE_ROW}`);
    writeCell(ws.getCell(LAYOUT.TITLE_ROW, 1), 'DEBIT NOTE', STYLE.TITLE);
    ws.getRow(LAYOUT.TITLE_ROW).height = LAYOUT.TITLE_H;
    ws.getRow(LAYOUT.GAP_TOP_ROW).height = LAYOUT.GAP_H;
};

const writeLeftInfo = (ctx, { items, managers, searchParams }) => {
    const { ws, lastColIdx, toLetter } = ctx;
    const firstItem = items?.[0] || {};
    const vendorName = firstItem.vendor_name || '';
    const managerName = Array.isArray(managers) && managers.length > 0
        ? managers.join(', ')
        : (firstItem.manager_name || '');
    const periodStart = formatDateDot(searchParams?.startDate);
    const periodEnd = formatDateDot(searchParams?.endDate);

    const endIdx = Math.min(LAYOUT.LEFT_INFO_COLS, lastColIdx);
    const endLetter = toLetter(endIdx);
    const rows = [
        `Customer : ${vendorName}`,
        `Manager : ${managerName}`,
        `Date : ${periodStart} ~ ${periodEnd}`
    ];
    for (let i = 0; i < rows.length; i += 1) {
        const r = LAYOUT.INFO_START_ROW + i;
        if (endIdx > 1) ws.mergeCells(`A${r}:${endLetter}${r}`);
        writeCell(ws.getCell(`A${r}`), rows[i], STYLE.LEFT_INFO);
        ws.getRow(r).height = LAYOUT.INFO_H;
    }
    return { leftRows: rows, leftBlockEndIdx: endIdx };
};

const writeSupplierBlock = (ctx, leftBlockEndIdx) => {
    const { ws, lastColIdx, toLetter, measure } = ctx;
    if (lastColIdx - leftBlockEndIdx < LAYOUT.SUPPLIER_BLOCK_COLS) return null;

    const nameIdx = lastColIdx;
    const valEnd = lastColIdx - 1;
    const valStart = lastColIdx - 5;
    const labelIdx = valStart - 1;
    const supIdx = labelIdx - 1;

    measure(supIdx - 1, 'SUPPLIER');
    measure(nameIdx - 1, CEO_NAME);

    const supLetter = toLetter(supIdx);
    ws.mergeCells(`${supLetter}3:${supLetter}5`);
    writeCell(ws.getCell(`${supLetter}3`), 'SUPPLIER', STYLE.SUPPLIER_VERT);

    const labelLetter = toLetter(labelIdx);
    const valStartLetter = toLetter(valStart);
    const valEndLetter = toLetter(valEnd);
    for (let i = 0; i < SUPPLIER_ROWS.length; i += 1) {
        const [key, val] = SUPPLIER_ROWS[i];
        const r = LAYOUT.INFO_START_ROW + i;
        measure(labelIdx - 1, key);
        writeCell(ws.getCell(`${labelLetter}${r}`), key, STYLE.SUPPLIER_KEY);
        ws.mergeCells(`${valStartLetter}${r}:${valEndLetter}${r}`);
        writeCell(ws.getCell(`${valStartLetter}${r}`), val, STYLE.SUPPLIER_VAL);
    }

    const nameLetter = toLetter(nameIdx);
    ws.mergeCells(`${nameLetter}3:${nameLetter}5`);
    writeCell(ws.getCell(`${nameLetter}3`), CEO_NAME, STYLE.CEO);

    return { valStart, valEnd };
};

const writeHeaderRow = (ctx) => {
    const { ws, cols, lastColIdx, measure } = ctx;
    for (let i = 0; i < lastColIdx; i += 1) {
        const col = cols[i];
        const label = HEADER_LABEL_OVERRIDES[col.id] || col.label || col.id;
        measure(i, label);
        writeCell(ws.getCell(LAYOUT.HEADER_ROW, i + 1), label, STYLE.HEADER);
    }
    ws.getRow(LAYOUT.HEADER_ROW).height = LAYOUT.HEADER_H;
};

const expandItemsByLot = (items) => items.map((it, i) => ({ item: it, idx: i }));

const sortEntriesByGiReqDate = (entries) => entries
    .map((e, k) => {
        const ts = e.item?.gi_request_planned_dispatch_at
            ? dayjs(e.item.gi_request_planned_dispatch_at).valueOf()
            : Infinity;
        return { ...e, _k: k, _ts: Number.isFinite(ts) ? ts : Infinity };
    })
    .sort((a, b) => (a._ts - b._ts) || (a.idx - b.idx) || (a._k - b._k))
    .map(({ _k, _ts, ...rest }) => rest);

const writeDataRows = (ctx, entries) => {
    const { ws, cols, lastColIdx, measure } = ctx;
    let row = LAYOUT.HEADER_ROW + 1;
    let qtyCol1 = 0;
    let priceCol1 = 0;
    for (let i = 0; i < lastColIdx; i += 1) {
        if (cols[i].id === 'deliveryQty') qtyCol1 = i + 1;
        else if (cols[i].id === 'priceSum') priceCol1 = i + 1;
    }

    for (let idx = 0; idx < entries.length; idx += 1) {
        const { item } = entries[idx];
        let longText = false;
        for (let i = 0; i < lastColIdx; i += 1) {
            const col = cols[i];
            const v = getCellValueByColumnId(col.id, item, idx);
            if (measure(i, v) > LAYOUT.MAX_ONE_LINE) longText = true;
            const cell = ws.getCell(row, i + 1);
            const style = RIGHT_ALIGNED_IDS.has(col.id) ? STYLE.DATA_RIGHT : STYLE.DATA_CENTER;
            let numericCell = null;
            if (col.id === 'price' && item.price != null) {
                const n = Number(item.price);
                if (Number.isFinite(n)) numericCell = { value: n, numFmt: moneyNumFmtFor(item.price_currency, n) };
            } else if (col.id === 'priceSum' && item.price != null) {
                const n = Number(item.price) * Number(item.gi_request_line_qty || 0);
                if (Number.isFinite(n)) numericCell = { value: n, numFmt: moneyNumFmtFor(item.price_currency, n) };
            } else if (col.id === 'orderQty' && item.order_qty != null) {
                const n = Number(item.order_qty);
                if (Number.isFinite(n)) numericCell = { value: n, numFmt: qtyNumFmtFor(n) };
            } else if (col.id === 'deliveryQty') {
                const n = Number(item.gi_request_line_qty);
                if (Number.isFinite(n)) numericCell = { value: n, numFmt: qtyNumFmtFor(n) };
            }
            if (numericCell) {
                writeCell(cell, numericCell.value, style);
                cell.numFmt = numericCell.numFmt;
            } else {
                writeCell(cell, v, style);
                cell.numFmt = '@';
            }
        }
        if (!longText) ws.getRow(row).height = LAYOUT.DATA_H;
        row += 1;
    }
    return { nextRow: row, qtyCol1, priceCol1 };
};

const computeTotalsByCurrency = (entries) => {
    const totals = new Map();
    for (const { item } of entries) {
        const cur = item.price_currency || '';
        const unit = Number(item.price);
        const qty = Number(item.gi_request_line_qty || 0);
        const amt = Number.isFinite(unit) ? qty * unit : 0;
        const entry = totals.get(cur);
        if (entry) { entry.qty += qty; entry.price += amt; }
        else totals.set(cur, { qty, price: amt });
    }
    return totals;
};

const writeTotalsRows = (ctx, { entries, startRow, qtyCol1, priceCol1 }) => {
    if ((qtyCol1 === 0 && priceCol1 === 0) || entries.length === 0) return startRow;

    const { ws, toLetter, measure } = ctx;
    const totals = computeTotalsByCurrency(entries);

    let leftEnd = 0;
    if (qtyCol1 > 0 && priceCol1 > 0) leftEnd = Math.min(qtyCol1, priceCol1) - 1;
    else if (qtyCol1 > 0) leftEnd = qtyCol1 - 1;
    else if (priceCol1 > 0) leftEnd = priceCol1 - 1;

    let row = startRow;
    for (const [cur, { qty, price }] of totals) {
        const sym = cur ? `${getCurrencySymbol(cur)} ` : '';
        if (leftEnd >= 1) {
            if (leftEnd > 1) ws.mergeCells(`A${row}:${toLetter(leftEnd)}${row}`);
            writeCell(ws.getCell(row, 1), 'TOTAL', STYLE.TOTAL_LABEL);
        }
        if (qtyCol1 > 0) {
            const val = formatNumber(qty);
            measure(qtyCol1 - 1, val);
            const cell = ws.getCell(row, qtyCol1);
            writeCell(cell, Number(qty) || 0, STYLE.TOTAL_VAL);
            cell.numFmt = qtyNumFmtFor(qty);
        }
        if (priceCol1 > 0) {
            const val = `${sym}${formatNumber(price)}`;
            measure(priceCol1 - 1, val);
            const cell = ws.getCell(row, priceCol1);
            writeCell(cell, Number(price) || 0, STYLE.TOTAL_VAL);
            cell.numFmt = moneyNumFmtFor(cur, price);
        }
        ws.getRow(row).height = LAYOUT.TOTAL_H;
        row += 1;
    }
    return row;
};

const applyColumnWidths = (ctx) => {
    const { ws, cols, lastColIdx, maxLenByCol } = ctx;
    for (let i = 0; i < lastColIdx; i += 1) {
        const effective = maxLenByCol[i] || 10;
        const content = Math.min(LAYOUT.MAX_ONE_LINE, effective) + LAYOUT.WIDTH_PADDING;
        const min = COLUMN_MIN_WIDTHS[cols[i].id] ?? 6;
        ws.getColumn(i + 1).width = Math.max(min, content);
    }
};

const expandLastColToFit = (ws, startCol, endCol, requiredWidth) => {
    const sum = sumColumnWidths(ws, startCol, endCol);
    if (sum >= requiredWidth) return;
    const last = ws.getColumn(endCol);
    last.width = (last.width || 0) + (requiredWidth - sum);
};

const distributeExtraWidth = (ws, startCol, endCol, requiredWidth) => {
    const sum = sumColumnWidths(ws, startCol, endCol);
    if (sum >= requiredWidth) return;
    const diff = requiredWidth - sum;
    const count = endCol - startCol + 1;
    const per = Math.floor(diff / count);
    const rest = diff - per * count;
    for (let i = startCol; i <= endCol; i += 1) {
        const col = ws.getColumn(i);
        col.width = (col.width || 0) + per;
    }
    if (rest > 0) {
        const last = ws.getColumn(endCol);
        last.width = (last.width || 0) + rest;
    }
};

const writeBankBlock = (ctx, bankRow) => {
    const { ws, lastColIdx, toLetter } = ctx;
    if (lastColIdx < 3) return null;

    let bankMax = 0;
    for (const t of BANK_LINES) if (t.length > bankMax) bankMax = t.length;
    const need = bankMax + LAYOUT.WIDTH_PADDING;

    let mergeEnd = Math.min(3, lastColIdx);
    let sum = sumColumnWidths(ws, 1, mergeEnd);
    while (sum < need && mergeEnd < lastColIdx) {
        mergeEnd += 1;
        sum += ws.getColumn(mergeEnd).width || 0;
    }

    if (mergeEnd > 1) ws.mergeCells(`A${bankRow}:${toLetter(mergeEnd)}${bankRow}`);
    writeCell(ws.getCell(bankRow, 1), BANK_LINES.join('\n'), STYLE.BANK);
    return mergeEnd;
};

const placeSignature = async (ctx, { bankRow, mergeEnd, qtyCol1, priceCol1 }) => {
    try {
        const signBase64 = await imageToBase64Raw(signImage);
        if (!signBase64) return;
        const imageId = ctx.workbook.addImage({ base64: signBase64, extension: 'jpeg' });
        let col = mergeEnd;
        const qty = qtyCol1 > 0 ? qtyCol1 - 1 : -1;
        const price = priceCol1 > 0 ? priceCol1 - 1 : -1;
        if (qty >= 0 && price >= 0) col = Math.min(qty, price);
        else if (qty >= 0) col = qty - 1;
        else if (price >= 0) col = price - 1;
        if (col < mergeEnd) col = mergeEnd;
        ctx.ws.addImage(imageId, {
            tl: { col: col + 0.1, row: bankRow - 1 + 0.05 },
            ext: { width: LAYOUT.SIGN_WIDTH, height: LAYOUT.SIGN_HEIGHT }
        });
    } catch (_) {
        void 0;
    }
};

const triggerDownload = async (workbook) => {
    const buffer = await workbook.xlsx.writeBuffer();
    const tag = dayjs().format('YYYYMMDD_HHmmss');
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `DEBIT NOTE ${tag}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
};

export const exportDebitNote = async ({ items, columns, searchParams, managers, downloadIndicatorRef, includeSign = true }) => {
    downloadIndicatorRef?.current?.startDownload?.();
    try {
        const workbook = new ExcelJS.Workbook();
        const ws = workbook.addWorksheet('DEBIT NOTE');
        const cols = resolveColumns(columns);
        const ctx = createContext(workbook, ws, cols);

        writeTitle(ctx);
        const { leftRows, leftBlockEndIdx } = writeLeftInfo(ctx, { items, managers, searchParams });
        const supplierRange = writeSupplierBlock(ctx, leftBlockEndIdx);
        ws.getRow(LAYOUT.GAP_MID_ROW).height = LAYOUT.GAP_H;
        writeHeaderRow(ctx);

        const rawItems = items || [];
        const entries = sortEntriesByGiReqDate(expandItemsByLot(rawItems));

        const { nextRow: afterData, qtyCol1, priceCol1 } = writeDataRows(ctx, entries);
        const afterTotals = writeTotalsRows(ctx, {
            entries,
            startRow: afterData, qtyCol1, priceCol1
        });

        ws.getRow(afterTotals).height = LAYOUT.GAP_H;
        const bankRow = afterTotals + 1;
        ws.getRow(bankRow).height = LAYOUT.BANK_H;

        applyColumnWidths(ctx);

        let leftInfoMax = 0;
        for (const t of leftRows) if (t.length > leftInfoMax) leftInfoMax = t.length;
        if (leftBlockEndIdx >= 1 && leftInfoMax > 0) {
            expandLastColToFit(ws, 1, leftBlockEndIdx, Math.min(LAYOUT.MAX_ONE_LINE, leftInfoMax) + LAYOUT.WIDTH_PADDING);
        }
        if (supplierRange) {
            distributeExtraWidth(ws, supplierRange.valStart, supplierRange.valEnd, ADDRESS.length + LAYOUT.WIDTH_PADDING);
        }

        const mergeEnd = writeBankBlock(ctx, bankRow);
        if (mergeEnd != null && includeSign) {
            await placeSignature(ctx, { bankRow, mergeEnd, qtyCol1, priceCol1 });
        }

        await triggerDownload(workbook);
    } finally {
        if (downloadIndicatorRef?.current?.completeDownload) {
            setTimeout(() => downloadIndicatorRef.current.completeDownload(), 100);
        }
    }
};
